import { useMemo } from "react";
import { darkTheme, styled } from "~/ui/style/stitches.config";
import { imageUrl } from "~/utils/imageurl";
import { findMatchingCardsFromOtherDecks, MatchingCard } from "~/utils/cardMatchingUtils";
import { allDecks } from "~/ui/menus/DeckMenu";
import { deckNameToDeckPageId } from "~/pages/marketplace/DeckMarketplace";
import { trackEvent } from "~/api/analyticsApi";

interface CardArtVariationsProps {
  cardName: string;
  title?: React.ReactNode;
}

export const CardArtVariations = ({ cardName, title }: CardArtVariationsProps) => {
  const matchingCards = useMemo(() => {
    return findMatchingCardsFromOtherDecks(cardName, allDecks, 3);
  }, [cardName]);

  if (matchingCards.length === 0) {
    return null;
  }

  return (
    <Container>
      {title}
      <CardRow>
        {matchingCards.map((match: MatchingCard) => (
          <CardWrapper key={`${match.deck.id}-${match.card.name}`}>
            <CardImage
              src={imageUrl(match.imagePath, 180)}
              alt={`${match.card.name} from ${match.deck.name}`}
              loading="lazy"
            />
            <DeckName onClick={() => {
              trackEvent("/guidebook/pressed_deck", { deckName: match.deck.name });
              window.location.href = `/decks/${deckNameToDeckPageId(match.deck.name)}`;
            }}>
              {match.deck.name}
            </DeckName>
          </CardWrapper>
        ))}
      </CardRow>
    </Container>
  );
};

const Container = styled("div", {
  marginTop: "20px",
  width: "100%",
});

const CardRow = styled("div", {
  display: "flex",
  flexDirection: "row",
  marginTop: "12px",
  justifyContent: "center",
  gap: "16px",
  width: "100%",
});

const CardWrapper = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "90px",
});

const CardImage = styled("img", {
  width: "auto",
  height: "130px",
  borderRadius: "6px",
  border: ".5px solid rgba(0, 0, 0, 0.8)",
  transition: "transform 0.2s ease",
  "&:hover": {
    transform: "perspective(400px) rotateX(10deg) translateY(-3px)",
  },
  [`.${darkTheme.className} &`]: {
    border: ".5px solid rgba(255, 255, 255, 0.3)",
  },
});

const DeckName = styled("div", {
  fontSize: "9px",
  fontFamily: "worksans",
  color: "rgba(0, 0, 0, 0.8)",
  textAlign: "center",
  marginTop: "10px",
  maxWidth: "80px",
  lineHeight: "1.2",
  textDecoration: "underline",
  textUnderlineOffset: "2px",
  cursor: "pointer",
  "@mobile": {
    fontSize: "11px",
  },
  "&:hover": {
    opacity: "0.6",
  },
  [`.${darkTheme.className} &`]: {
    color: "$text",
  },
});

